import { useEffect, useState, useCallback } from "react";
import { Save, Gift, RefreshCcw, AlertCircle, CheckCircle2, History, Percent } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import api from "../lib/api";
import usePagedRows from "../components/usePagedRows";
import Pagination from "../components/Pagination";
import { fmtBDT } from "../lib/format";

const DEFAULTS = {
  enabled: false,
  bonus_pct: 0,
  min_deposit: 0,
  max_bonus: 0,
  first_deposit_only: false,
  bonus_turnover_multiplier: 2,
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function ConfigAgentBonus() {
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState({ items: [], totals: { count: 0, total_amount: 0 } });
  const [historyLoading, setHistoryLoading] = useState(true);
  const histPaged = usePagedRows(history.items, 12);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/agent-deposit-bonus");
      setForm({ ...DEFAULTS, ...data });
    } catch (e) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get("/reports/agent-deposit-bonus/history", { params: { limit: 50 } });
      setHistory(data || { items: [], totals: { count: 0, total_amount: 0 } });
    } catch (e) {
      // silent
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
    loadHistory();
  }, [loadSettings, loadHistory]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (form.bonus_pct < 0 || form.bonus_pct > 100) {
      toast.error("Bonus % must be between 0 and 100");
      return;
    }
    if (form.min_deposit < 0 || form.max_bonus < 0 || form.bonus_turnover_multiplier < 0) {
      toast.error("Amounts cannot be negative");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/settings/agent-deposit-bonus", {
        enabled: !!form.enabled,
        bonus_pct: Number(form.bonus_pct) || 0,
        min_deposit: Number(form.min_deposit) || 0,
        max_bonus: Number(form.max_bonus) || 0,
        first_deposit_only: !!form.first_deposit_only,
        bonus_turnover_multiplier: Number(form.bonus_turnover_multiplier) || 0,
      });
      setForm({ ...DEFAULTS, ...data });
      toast.success("Agent-deposit bonus settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Live preview
  const sampleDeposit = Math.max(Number(form.min_deposit) || 100, 100);
  const computed = (sampleDeposit * (Number(form.bonus_pct) || 0)) / 100;
  const capped = Number(form.max_bonus) > 0 ? Math.min(computed, Number(form.max_bonus)) : computed;
  const turnoverAdded = capped * (Number(form.bonus_turnover_multiplier) || 0);

  if (loading) {
    return <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">Loading…</div>;
  }

  return (
    <div data-testid="config-agent-bonus-page">
      <PageHeader
        title="Agent Deposit Bonus"
        subtitle="Instant bonus credited to players when they receive a deposit from any agent"
      />

      <div className="space-y-5 max-w-5xl">
        {/* Master toggle banner */}
        <div
          className={`rounded-sm border p-4 flex items-start gap-3 ${
            form.enabled ? "bg-emerald-50 border-emerald-200" : "bg-gray-50 border-gray-200"
          }`}
        >
          {form.enabled ? (
            <CheckCircle2 size={18} className="text-emerald-700 mt-0.5 flex-shrink-0" />
          ) : (
            <AlertCircle size={18} className="text-gray-500 mt-0.5 flex-shrink-0" />
          )}
          <div className="flex-1">
            <div className={`text-sm font-bold ${form.enabled ? "text-emerald-900" : "text-gray-700"}`}>
              {form.enabled ? "Bonus is LIVE" : "Bonus is OFF"}
            </div>
            <div className="text-[12.5px] text-gray-700 mt-0.5 leading-relaxed">
              {form.enabled
                ? `Players receive ${form.bonus_pct}% instant bonus on every agent deposit ≥ ৳${Number(form.min_deposit).toLocaleString()}${form.first_deposit_only ? " (first agent-deposit only)" : ""}.`
                : "Toggle below and configure rules to start crediting agent-deposit bonuses automatically."}
            </div>
          </div>
          <label className="inline-flex items-center cursor-pointer select-none ml-3">
            <input
              type="checkbox"
              data-testid="agent-bonus-enabled"
              checked={!!form.enabled}
              onChange={(e) => set("enabled", e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-300 peer-checked:bg-emerald-500 rounded-full transition-colors relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5" />
          </label>
        </div>

        {/* Rules grid */}
        <Section title="Bonus Rules" icon={Gift}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Bonus % of deposit amount" hint="e.g. 10 means a ৳1,000 deposit credits ৳100 bonus">
              <div className="relative">
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={form.bonus_pct}
                  onChange={(e) => set("bonus_pct", parseFloat(e.target.value) || 0)}
                  className="w-full h-10 pl-3 pr-9 border border-gray-200 rounded-sm text-sm tabular focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                  data-testid="agent-bonus-pct"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">
                  <Percent size={14} />
                </span>
              </div>
            </Field>

            <Field label="Minimum deposit to qualify (BDT)" hint="Set 0 to allow all deposits">
              <CurrencyInput
                value={form.min_deposit}
                onChange={(v) => set("min_deposit", v)}
                testid="agent-bonus-min-deposit"
              />
            </Field>

            <Field label="Max bonus per deposit (BDT)" hint="0 = no cap. e.g. 500 caps even if 10% × big deposit > 500.">
              <CurrencyInput
                value={form.max_bonus}
                onChange={(v) => set("max_bonus", v)}
                testid="agent-bonus-max-bonus"
              />
            </Field>

            <Field
              label="Turnover multiplier on bonus"
              hint="Bonus × multiplier added to user's wagering requirement. e.g. 2× = ৳50 bonus needs ৳100 wagered. 0 = no turnover lock."
            >
              <div className="relative">
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={form.bonus_turnover_multiplier}
                  onChange={(e) => set("bonus_turnover_multiplier", parseFloat(e.target.value) || 0)}
                  className="w-full h-10 pl-3 pr-9 border border-gray-200 rounded-sm text-sm tabular focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                  data-testid="agent-bonus-turnover-mult"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-bold pointer-events-none">
                  ×
                </span>
              </div>
            </Field>

            <Field label="Apply on user's first agent-deposit only?" hint="If ON, bonus credits only the first time a user receives an agent deposit">
              <label className="flex items-center gap-3 h-10 px-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  data-testid="agent-bonus-first-only"
                  checked={!!form.first_deposit_only}
                  onChange={(e) => set("first_deposit_only", e.target.checked)}
                />
                <span className="text-sm font-medium text-gray-800">
                  {form.first_deposit_only ? "Yes — first agent-deposit only" : "No — every agent deposit qualifies"}
                </span>
              </label>
            </Field>
          </div>

          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={loadSettings}
              disabled={saving}
              className="h-10 px-4 inline-flex items-center gap-2 text-sm font-semibold rounded-sm border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              data-testid="agent-bonus-reset"
            >
              <RefreshCcw size={14} /> Reload
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="agent-bonus-save"
            >
              <Save size={14} /> {saving ? "Saving…" : "Save settings"}
            </button>
          </div>
        </Section>

        {/* Live preview */}
        <Section title="Live Preview" icon={Gift} accent="amber">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-sm border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <div className="text-[11px] uppercase tracking-widest font-semibold opacity-70 mb-1">Sample scenario</div>
              <div className="space-y-1.5 text-[13px]">
                <div>Player receives <b className="tabular">{fmtBDT(sampleDeposit)}</b> from an agent</div>
                <div>
                  Bonus calculation: <b className="tabular">{fmtBDT(sampleDeposit)}</b> × {form.bonus_pct}% =
                  {" "}<b className="tabular">{fmtBDT(computed)}</b>
                </div>
                {Number(form.max_bonus) > 0 && computed > Number(form.max_bonus) && (
                  <div className="text-[12px] text-amber-700">
                    Capped at max-bonus → <b className="tabular">{fmtBDT(capped)}</b>
                  </div>
                )}
                <div className="border-t border-amber-200 pt-1.5 mt-2">
                  Player wallet credited: <b className="tabular text-emerald-700">+{fmtBDT(capped)}</b>
                </div>
                <div>
                  Turnover added: <b className="tabular">{fmtBDT(turnoverAdded)}</b>{" "}
                  <span className="opacity-70">({fmtBDT(capped)} × {form.bonus_turnover_multiplier})</span>
                </div>
              </div>
            </div>
            <div className="rounded-sm border border-gray-200 bg-gray-50 p-4 text-[12.5px] text-gray-700 leading-relaxed">
              <div className="font-semibold text-gray-900 mb-2 text-sm">How it works</div>
              <ol className="list-decimal pl-4 space-y-1.5">
                <li>Agent deposits to player from agent panel.</li>
                <li>Player wallet credited with deposit amount instantly.</li>
                <li>If <b>enabled</b> + deposit ≥ <b>min</b> + (first-only check passes) → bonus auto-credited.</li>
                <li>Bonus shows in player's transaction history as a separate row (type: bonus).</li>
                <li>Turnover requirement increases by <b>(deposit × 1)</b> + <b>(bonus × multiplier)</b>.</li>
                <li>Player must wager total turnover before withdrawing.</li>
              </ol>
            </div>
          </div>
        </Section>

        {/* Recent payouts */}
        <Section title="Recent Bonus Payouts" icon={History}>
          <div className="flex items-center justify-between mb-3">
            <div className="text-[12.5px] text-gray-700">
              Total payouts: <b className="tabular">{history.totals.count}</b> · Total credited:{" "}
              <b className="tabular text-emerald-700">{fmtBDT(history.totals.total_amount)}</b>
            </div>
            <button
              type="button"
              onClick={loadHistory}
              disabled={historyLoading}
              className="h-8 px-3 inline-flex items-center gap-1.5 text-xs font-semibold rounded-sm border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              data-testid="agent-bonus-history-refresh"
            >
              <RefreshCcw size={12} /> Refresh
            </button>
          </div>

          <div className="overflow-x-auto border border-gray-100 rounded-sm">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Player</th>
                  <th className="text-left px-3 py-2">Agent</th>
                  <th className="text-right px-3 py-2">Bonus</th>
                  <th className="text-left px-3 py-2">Reference</th>
                </tr>
              </thead>
              <tbody data-testid="agent-bonus-history-table">
                {historyLoading && (
                  <tr><td colSpan={5} className="px-3 py-8 text-center text-gray-500">Loading…</td></tr>
                )}
                {!historyLoading && history.items.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-8 text-center text-gray-500">
                      No bonus payouts yet. They'll appear here as agents start depositing once the bonus is enabled.
                    </td>
                  </tr>
                )}
                {history.items.length > 0 && histPaged.visible.map((t) => (
                  <tr key={t.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 tabular text-[12px] text-gray-600 whitespace-nowrap">{fmtDateTime(t.created_at)}</td>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-gray-900">{t.username || "—"}</div>
                      <div className="text-[11px] text-gray-500 tabular">ID {t.player_id || "—"}</div>
                    </td>
                    <td className="px-3 py-2 text-gray-700">{t.agent_username || "—"}</td>
                    <td className="px-3 py-2 text-right tabular text-emerald-700 font-semibold">{fmtBDT(t.amount)}</td>
                    <td className="px-3 py-2 text-[11px] tabular text-gray-500">{t.reference || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination total={histPaged.totalRows} page={histPaged.page} pageSize={histPaged.pageSize} onChange={histPaged.setPage} testIdPrefix="agent-bonus-hist-pg" />
        </Section>
      </div>
    </div>
  );
}

function CurrencyInput({ value, onChange, testid }) {
  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-bold pointer-events-none">৳</span>
      <input
        type="number"
        step="0.01"
        min="0"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="w-full h-10 pl-7 pr-3 border border-gray-200 rounded-sm text-sm tabular focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
        data-testid={testid}
      />
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-gray-500 mt-1 leading-snug">{hint}</p>}
    </div>
  );
}

function Section({ title, icon: Icon, accent = "emerald", children }) {
  const accentColor = accent === "amber" ? "text-amber-500" : "text-emerald-500";
  return (
    <div className="bg-white border border-gray-200 rounded-sm p-5">
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
        {Icon && <Icon size={14} className={accentColor} />}
        <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">{title}</div>
      </div>
      {children}
    </div>
  );
}
